/**
 * @file Frontend API client — characterizationFactorsAdminService.
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";

/** Type definition — Characterization Factor Admin. */
export interface CharacterizationFactorAdmin {
  id: number;
  fuelMix: string;
  unit: string;
  characterizationFactor: string;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Type definition — Characterization Factor Create Input. */
export interface CharacterizationFactorCreateInput {
  fuelMix: string;
  unit: string;
  characterizationFactor: string;
  sortOrder?: number;
  isActive?: boolean;
}

/** Type definition — Characterization Factor Update Input. */
export type CharacterizationFactorUpdateInput = Partial<CharacterizationFactorCreateInput>;

function apiMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    "Request failed"
  );
}

/** Exported const — characterization Factors Admin Service. */
export const characterizationFactorsAdminService = {
  async list(): Promise<CharacterizationFactorAdmin[]> {
    try {
      const res = await axiosClient.get<{ success?: boolean; data?: CharacterizationFactorAdmin[] }>(
        API_URLS.ADMIN.CHARACTERIZATION_FACTORS.LIST
      );
      if (res.data?.success === false) {
        throw new Error((res.data as { message?: string }).message || "Failed to list factors");
      }
      const rows = res.data?.data;
      return Array.isArray(rows) ? rows : [];
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async getOne(id: number): Promise<CharacterizationFactorAdmin> {
    try {
      const res = await axiosClient.get<{ success?: boolean; data?: CharacterizationFactorAdmin }>(
        API_URLS.ADMIN.CHARACTERIZATION_FACTORS.BY_ID(id)
      );
      if (res.data?.success === false || !res.data?.data) {
        throw new Error((res.data as { message?: string })?.message || "Not found");
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async create(body: CharacterizationFactorCreateInput): Promise<CharacterizationFactorAdmin> {
    try {
      const res = await axiosClient.post<{ success?: boolean; data?: CharacterizationFactorAdmin }>(
        API_URLS.ADMIN.CHARACTERIZATION_FACTORS.LIST,
        body
      );
      if (res.data?.success === false || !res.data?.data) {
        throw new Error((res.data as { message?: string })?.message || "Create failed");
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async update(
    id: number,
    body: CharacterizationFactorUpdateInput
  ): Promise<CharacterizationFactorAdmin> {
    try {
      const res = await axiosClient.put<{ success?: boolean; data?: CharacterizationFactorAdmin }>(
        API_URLS.ADMIN.CHARACTERIZATION_FACTORS.BY_ID(id),
        body
      );
      if (res.data?.success === false || !res.data?.data) {
        throw new Error((res.data as { message?: string })?.message || "Update failed");
      }
      return res.data.data;
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },

  async delete(id: number): Promise<void> {
    try {
      const res = await axiosClient.delete<{ success?: boolean; message?: string }>(
        API_URLS.ADMIN.CHARACTERIZATION_FACTORS.BY_ID(id)
      );
      if (res.data?.success === false) {
        throw new Error(res.data.message || "Delete failed");
      }
    } catch (err) {
      throw new Error(apiMessage(err));
    }
  },
};
